/*
 * ET - Product Slider (sections/et-product-slider.liquid)
 * <et-product-slider> creates one Swiper (window.etLoadSwiper from et-base-head) when connected and destroys it when
 * disconnected, so theme editor re-renders never leave a duplicate instance. Slide widths come from CSS
 * (324px / 280px), so Swiper runs with slidesPerView 'auto'. Arrows and the progress pill hide when Swiper is locked;
 * the progress thumb keeps the Figma width (39px) and travels along the track.
 * Autoplay (data-autoplay / data-autoplay-delay, off by default): rewinds at the end and never runs with
 * prefers-reduced-motion. Accessibility (WCAG 2.2.2): it pauses while the mouse is over the slider or keyboard focus is
 * inside it (resumes when both leave), and stops for good once the visitor interacts (touch or mouse press, swipe,
 * arrow, progress bar drag). In the theme editor it stops while a block is selected and restarts on deselect.
 * Recommendations source: the element carries data-recommendations-url (Section Rendering, same request as the theme's
 * product-recommendations element). The fetched slides replace the empty track before Swiper starts; with no results
 * the section is removed (kept in the theme editor).
 */
(function () {
  'use strict';

  if (!window.customElements || window.customElements.get('et-product-slider')) return;

  const SLIDE_SELECTOR = '[data-et-ps-slide]';
  const TRACK_SELECTOR = '[data-et-ps-track]';
  const PROGRESS_THUMB_SIZE = 39; /* Figma progress pill: 39px thumb in the 96px track */

  class EtProductSlider extends HTMLElement {
    connectedCallback() {
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this.init(), { once: true });
      } else {
        this.init();
      }
    }

    disconnectedCallback() {
      this.destroy();
    }

    get options() {
      const prevEl = this.querySelector('[data-et-ps-prev]');
      const nextEl = this.querySelector('[data-et-ps-next]');
      const progressEl = this.querySelector('[data-et-ps-progress]');
      const delay = parseInt(this.dataset.autoplayDelay, 10) || 5000;
      const reducedMotion =
        typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const autoplay = this.dataset.autoplay === 'true' && !reducedMotion;

      return {
        slidesPerView: 'auto',
        spaceBetween: 16,
        speed: 400,
        watchOverflow: true,
        grabCursor: true,
        rewind: autoplay,
        autoplay: autoplay ? { delay, disableOnInteraction: true, pauseOnMouseEnter: true } : false,
        navigation: prevEl && nextEl ? { prevEl, nextEl } : false,
        scrollbar: progressEl ? { el: progressEl, draggable: true, dragSize: PROGRESS_THUMB_SIZE } : false,
        a11y: {
          enabled: true,
          prevSlideMessage: prevEl ? prevEl.getAttribute('aria-label') : undefined,
          nextSlideMessage: nextEl ? nextEl.getAttribute('aria-label') : undefined
        }
      };
    }

    async init() {
      if (this.swiper || this.loading) return;
      this.loading = true;

      if (this.dataset.recommendationsUrl && !this.recommendationsLoaded) {
        const hasSlides = await this.loadRecommendations();
        if (!hasSlides) {
          this.loading = false;
          return;
        }
      }

      const Swiper = typeof window.etLoadSwiper === 'function' ? await window.etLoadSwiper() : null;
      this.loading = false;

      const swiperEl = this.querySelector('[data-et-ps-swiper]');
      if (!Swiper || !swiperEl || !this.isConnected || this.swiper) return;
      if (!swiperEl.querySelector(SLIDE_SELECTOR)) return;

      this.swiper = new Swiper(swiperEl, this.options);
      if (this.swiper.params.autoplay && this.swiper.params.autoplay.enabled) this.bindAutoplayControls();
    }

    bindAutoplayControls() {
      const swiper = this.swiper;
      this.autoplayHalted = false;
      this.autoplayFocusPaused = false;

      const halt = () => {
        this.autoplayHalted = true;
        if (swiper.autoplay.running) swiper.autoplay.stop();
      };
      swiper.on('touchStart scrollbarDragStart navigationNext navigationPrev', halt);

      this.onAutoplayFocusIn = () => {
        if (this.autoplayHalted || this.autoplayFocusPaused) return;
        this.autoplayFocusPaused = true;
        if (swiper.autoplay.running) swiper.autoplay.stop();
      };
      this.onAutoplayFocusOut = (event) => {
        if (event.relatedTarget instanceof Node && this.contains(event.relatedTarget)) return;
        if (!this.autoplayFocusPaused) return;
        this.autoplayFocusPaused = false;
        if (this.autoplayHalted) return;
        this.startAutoplay();
      };
      this.addEventListener('focusin', this.onAutoplayFocusIn);
      this.addEventListener('focusout', this.onAutoplayFocusOut);
    }

    unbindAutoplayControls() {
      if (this.onAutoplayFocusIn) this.removeEventListener('focusin', this.onAutoplayFocusIn);
      if (this.onAutoplayFocusOut) this.removeEventListener('focusout', this.onAutoplayFocusOut);
      this.onAutoplayFocusIn = null;
      this.onAutoplayFocusOut = null;
      this.autoplayFocusPaused = false;
    }

    startAutoplay() {
      const swiper = this.swiper;
      if (!swiper || swiper.destroyed || !swiper.autoplay || swiper.autoplay.running) return;
      swiper.autoplay.paused = false;
      swiper.autoplay.start();
      /* Mouse still over the slider: keep it paused until the pointer leaves (Swiper resumes on pointerleave) */
      if (swiper.params.autoplay.pauseOnMouseEnter && swiper.el.matches(':hover')) swiper.autoplay.pause(true);
    }

    async loadRecommendations() {
      this.recommendationsLoaded = true;
      const track = this.querySelector(TRACK_SELECTOR);

      try {
        const response = await fetch(this.dataset.recommendationsUrl, { priority: 'low' });
        if (!response.ok) throw new Error(`Recommendations request failed (${response.status})`);

        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const fetchedTrack = html.querySelector(TRACK_SELECTOR);

        if (track && fetchedTrack && fetchedTrack.querySelector(SLIDE_SELECTOR)) {
          track.replaceChildren(...document.importNode(fetchedTrack, true).childNodes);
          return this.isConnected;
        }
      } catch (error) {
        console.warn('ET - Product Slider:', error);
      }

      this.removeEmptySection();
      return false;
    }

    removeEmptySection() {
      if (window.Shopify && window.Shopify.designMode) return;
      const section = this.closest('.shopify-section');
      (section || this).remove();
    }

    destroy() {
      if (!this.swiper) return;
      this.unbindAutoplayControls();
      this.swiper.destroy(true, true);
      this.swiper = null;
    }

    selectSlide(slide) {
      if (!this.swiper) return;
      if (this.swiper.autoplay && this.swiper.autoplay.running) this.swiper.autoplay.stop();
      const index = this.swiper.slides.indexOf(slide);
      if (index > -1) this.swiper.slideTo(index, 0);
    }

    resumeAutoplay() {
      if (!this.swiper || !this.swiper.params.autoplay || !this.swiper.params.autoplay.enabled) return;
      this.autoplayHalted = false;
      this.startAutoplay();
    }
  }

  window.customElements.define('et-product-slider', EtProductSlider);

  const slidersIn = (scope) =>
    scope instanceof Element || scope === document ? Array.from(scope.querySelectorAll('et-product-slider')) : [];

  document.addEventListener('shopify:section:load', (event) => slidersIn(event.target).forEach((el) => el.init()));
  document.addEventListener('shopify:section:unload', (event) => slidersIn(event.target).forEach((el) => el.destroy()));

  const sliderSlide = (event) => {
    const slide = event.target instanceof Element ? event.target.closest(SLIDE_SELECTOR) : null;
    return slide ? [slide, slide.closest('et-product-slider')] : [null, null];
  };

  document.addEventListener('shopify:block:select', (event) => {
    const [slide, slider] = sliderSlide(event);
    if (slider) slider.selectSlide(slide);
  });

  document.addEventListener('shopify:block:deselect', (event) => {
    const [, slider] = sliderSlide(event);
    if (slider) slider.resumeAutoplay();
  });
})();
